import type { Lead } from '@up2stay/up2promo-api-client';
import { Badge } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { formatLeadDisplayName } from '@up2stay/up2promo-commons';
import { Mail, Phone } from 'lucide-react';

import {
  type LeadCardLabelMaps,
  LeadCardLabels,
} from '@/core/components/leads/lead-card-labels';
import { LeadStatusBadge } from '@/core/components/leads/lead-detail/lead-status-badge';

/** Extra CRM/filter context on workspace-scoped list cards */
export interface LeadCardMetaLines {
  creatorLabel: string;
  creatorValue: string;
  assigneeLabel: string;
  assigneeValue: string;
  sourceLabel: string;
  sourceValue: string;
  visibility: 'shared' | 'private' | null;
  visibilitySharedLabel: string;
  visibilityPrivateLabel: string;
}

/** Shared meta row for list cards and Kanban cards */
export function LeadCardMetaDetails({ meta }: { meta: LeadCardMetaLines }) {
  const segment = 'max-w-full truncate';

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] leading-snug tracking-[0.02em]">
      <span className={segment}>
        <span className="text-muted-foreground">{meta.creatorLabel}: </span>
        <span className="font-semibold text-foreground">
          {meta.creatorValue}
        </span>
      </span>
      <span className="text-muted-foreground/40" aria-hidden>
        ·
      </span>
      <span className={segment}>
        <span className="text-muted-foreground">{meta.assigneeLabel}: </span>
        <span className="font-semibold text-foreground">
          {meta.assigneeValue}
        </span>
      </span>
      <span className="text-muted-foreground/40" aria-hidden>
        ·
      </span>
      <span className={segment}>
        <span className="text-muted-foreground">{meta.sourceLabel}: </span>
        <span className="font-semibold text-foreground">
          {meta.sourceValue}
        </span>
      </span>
      {meta.visibility ? (
        <>
          <span className="text-muted-foreground/40" aria-hidden>
            ·
          </span>
          <Badge
            variant={meta.visibility === 'shared' ? 'success' : 'warning'}
            className="h-5 px-1.5 py-0 text-[10px] font-normal"
          >
            {meta.visibility === 'shared'
              ? meta.visibilitySharedLabel
              : meta.visibilityPrivateLabel}
          </Badge>
        </>
      ) : null}
    </div>
  );
}

interface LeadCardContentProps {
  lead: Lead;
  getStatusLabel: (status: string) => string;
  /** Compact mode for Kanban cards (less padding, single-line clamp where useful) */
  compact?: boolean;
  meta?: LeadCardMetaLines | null;
  labelMaps?: LeadCardLabelMaps;
}

export function LeadCardContent({
  lead,
  getStatusLabel,
  compact = false,
  meta,
  labelMaps,
}: LeadCardContentProps) {
  const padding = compact ? 'p-0' : 'p-4';

  return (
    <div className={`${padding} flex flex-col gap-1.5 text-left`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-foreground truncate">
          {formatLeadDisplayName(lead.firstName, lead.lastName) ||
            lead.email ||
            '—'}
        </span>
        <LeadStatusBadge
          status={lead.status}
          label={getStatusLabel(lead.status)}
          showDot={!compact}
          className={cn(compact && 'text-xs')}
        />
      </div>
      {lead.companyName && (
        <p className="text-sm text-muted-foreground truncate">
          {lead.companyName}
        </p>
      )}
      <div className="flex flex-col gap-0.5 text-sm text-muted-foreground">
        {lead.email && (
          <div className="flex items-center gap-2 min-w-0">
            <Mail className="h-3 w-3 shrink-0" />
            <span className="truncate">{lead.email}</span>
          </div>
        )}
        {lead.phone && (
          <div className="flex items-center gap-2 min-w-0">
            <Phone className="h-3 w-3 shrink-0" />
            <span className="truncate">{lead.phone}</span>
          </div>
        )}
      </div>
      <LeadCardLabels lead={lead} maps={labelMaps} compact={compact} />
      {meta ? <LeadCardMetaDetails meta={meta} /> : null}
      {lead.createdAt && (
        <p className="text-xs text-muted-foreground">
          {new Date(lead.createdAt).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>
      )}
    </div>
  );
}
